/**
 * Hierarchy Context
 * 
 * Provides React context for managing the multi-level organization hierarchy.
 * Works alongside CompanyContext - CompanyContext handles the currently selected
 * company while HierarchyContext manages the hierarchical structure and navigation.
 * 
 * BACKWARD COMPATIBILITY: Falls back gracefully when hierarchy features are not
 * configured for a user or company.
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { useCompany } from './CompanyContext';
import hierarchyService from '../services/hierarchyService';

// Debug flag - set to true for verbose console logging
const DEBUG = false;

// ============================================
// CONTEXT CREATION
// ============================================

const HierarchyContext = createContext(null);

/**
 * Hook to access hierarchy context
 * @returns {Object} Hierarchy context value
 */
export const useHierarchy = () => {
  const context = useContext(HierarchyContext);
  if (!context) {
    throw new Error('useHierarchy must be used within a HierarchyProvider');
  }
  return context;
};

// ============================================
// PROVIDER COMPONENT
// ============================================

export const HierarchyProvider = ({ children }) => {
  const { currentUser, userRole, isEmulating } = useAuth();
  const { companyData, companyIdForAddress } = useCompany();

  // ============================================
  // STATE
  // ============================================

  // Hierarchy tree state
  const [hierarchyTree, setHierarchyTree] = useState([]);
  const [flattenedTree, setFlattenedTree] = useState([]);

  // Visible companies (what the user can access)
  const [visibleCompanyIds, setVisibleCompanyIds] = useState([]);
  const [visibleCompanies, setVisibleCompanies] = useState([]);

  // Current hierarchy path (breadcrumb)
  const [currentHierarchyPath, setCurrentHierarchyPath] = useState([]);
  const [currentHierarchyPathDisplay, setCurrentHierarchyPathDisplay] = useState('');

  // Current company with hierarchy data
  const [currentCompanyWithHierarchy, setCurrentCompanyWithHierarchy] = useState(null);

  // Loading and error states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Feature flags
  const [hierarchyEnabled, setHierarchyEnabled] = useState(false);

  // ============================================
  // COMPUTED VALUES
  // ============================================

  /**
   * Whether the current user has hierarchy features enabled
   */
  const hasHierarchyAccess = useMemo(() => {
    if (!currentUser) return false;
    if (userRole === 'superadmin') return true;
    return currentUser.hierarchyAccess?.rootCompanyIds?.length > 0;
  }, [currentUser, userRole]);

  /**
   * Current user's hierarchy access mode
   */
  const accessMode = useMemo(() => {
    if (userRole === 'superadmin') return hierarchyService.ACCESS_MODES.FULL;
    return currentUser?.hierarchyAccess?.accessMode || hierarchyService.ACCESS_MODES.SINGLE;
  }, [currentUser, userRole]);

  /**
   * Current user's data visibility setting
   */
  const dataVisibility = useMemo(() => {
    if (userRole === 'superadmin') return hierarchyService.DATA_VISIBILITY.ALL;
    return currentUser?.hierarchyAccess?.dataVisibility || hierarchyService.DATA_VISIBILITY.OWN;
  }, [currentUser, userRole]);

  /**
   * Whether user can create sub-divisions
   */
  const canCreateDivisions = useMemo(() => {
    if (userRole === 'superadmin') return true;
    return currentUser?.canCreateSubDivisions || false;
  }, [currentUser, userRole]);

  /**
   * Get children of a specific company
   */
  const getCompanyChildren = useCallback((companyId) => {
    if (!companyId || flattenedTree.length === 0) return [];
    return flattenedTree.filter(c => c.parentCompanyId === companyId);
  }, [flattenedTree]);

  /**
   * Check if a company has children
   */
  const hasChildren = useCallback((companyId) => {
    return getCompanyChildren(companyId).length > 0;
  }, [getCompanyChildren]);

  // ============================================
  // DATA LOADING
  // ============================================

  /**
   * Load hierarchy data for the current user
   */
  const loadHierarchyData = useCallback(async () => {
    if (!currentUser) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Debug logging for emulation
      DEBUG && console.log('🔄 [HierarchyContext] Loading hierarchy data for user:', {
        email: currentUser.email,
        role: currentUser.role,
        userRole,
        isEmulating,
        hierarchyAccess: currentUser.hierarchyAccess,
        connectedCompanies: currentUser.connectedCompanies,
        connectedCompaniesArray: currentUser.connectedCompanies?.companies
      });

      let visibleIds;
      let companies;
      let tree = [];

      if (userRole === 'superadmin') {
        // FAST PATH: for a superadmin the visible ids, the full company objects, and the whole
        // tree all derive from the SAME "all companies" set. Read it ONCE and derive all three
        // in memory, instead of three serial full-collection scans (getVisibleCompanyIds +
        // getVisibleCompanies re-scan + getHierarchyTree(null)) that gated every /admin page.
        // Keep only real companies (a companyID-less/malformed doc must not appear in the
        // company list or collide on an undefined tree key). This keeps visibleCompanies
        // byte-identical to the old batched-`in` fetch, which also excluded such docs.
        companies = (await hierarchyService.getAllCompanies()).filter(c => c.companyID);
        visibleIds = companies.map(c => c.companyID);
        tree = hierarchyService.buildHierarchyTreeFromCompanies(companies);
      } else {
        // Compute the visible id set ONCE and reuse it for the full-object fetch — previously
        // getVisibleCompanies recomputed the entire set (a redundant descendant BFS pass).
        visibleIds = await hierarchyService.getVisibleCompanyIds(currentUser);
        companies = await hierarchyService.getVisibleCompanies(currentUser, visibleIds);

        // Descendant TREE building is opt-in exactly like descendant visibility:
        // only when accessMode is explicitly expansive ('full'/'branch'). The
        // admin UI mirrors rootCompanyIds onto EVERY user it saves (without an
        // accessMode), and getHierarchyTree BFS-walks the whole subtree at ONE
        // sequential Firestore query PER COMPANY — 20s+ against the master
        // company's tree, stalling every hierarchyLoading-gated page. Default
        // (single) users get a flat tree from the already-resolved visible
        // companies: zero extra reads and consistent with their visibility.
        const treeAccessMode = currentUser.hierarchyAccess?.accessMode;
        const wantsDescendantTree =
          currentUser.hierarchyAccess?.rootCompanyIds?.length > 0 &&
          (treeAccessMode === hierarchyService.ACCESS_MODES.FULL ||
            treeAccessMode === hierarchyService.ACCESS_MODES.BRANCH);

        if (wantsDescendantTree) {
          // User with expansive hierarchy access - build tree from their roots.
          // The descendant walk (parentCompanyId queries) is refused by the
          // companies rules for non-billing roles; fall back to the flat tree of
          // the already-resolved visible companies rather than failing the whole
          // hierarchy load.
          try {
            const branchTrees = [];
            for (const rootId of currentUser.hierarchyAccess.rootCompanyIds) {
              const branchTree = await hierarchyService.getHierarchyTree(rootId);
              branchTrees.push(...branchTree);
            }
            tree = branchTrees;
          } catch (treeError) {
            console.warn('[HierarchyContext] Descendant tree unavailable, using flat company list:', treeError?.code || treeError?.message || treeError);
            tree = companies.map(c => ({ ...c, children: [] }));
          }
        } else {
          // Default / legacy user - flat list of companies
          tree = companies.map(c => ({ ...c, children: [] }));
        }
      }

      DEBUG && console.log('🔄 [HierarchyContext] Visible company IDs:', visibleIds);
      setVisibleCompanyIds(visibleIds);
      setVisibleCompanies(companies);
      setHierarchyTree(tree);
      setFlattenedTree(hierarchyService.flattenHierarchyTree(tree));

      // Check if hierarchy features are being used
      const hasAnyHierarchy = companies.some(c => c.parentCompanyId || (c.childCompanyIds?.length > 0));
      setHierarchyEnabled(hasAnyHierarchy);

    } catch (err) {
      console.error('Error loading hierarchy data:', err);
      setError(err.message || 'Failed to load hierarchy data');
    } finally {
      setLoading(false);
    }
  }, [currentUser, userRole, isEmulating]);

  /**
   * Load current company's hierarchy path
   */
  const loadCurrentCompanyPath = useCallback(async () => {
    if (!companyIdForAddress) {
      setCurrentHierarchyPath([]);
      setCurrentHierarchyPathDisplay('');
      setCurrentCompanyWithHierarchy(null);
      return;
    }

    try {
      // Get company with hierarchy data
      const companyWithHierarchy = await hierarchyService.getCompanyWithHierarchy(companyIdForAddress);
      setCurrentCompanyWithHierarchy(companyWithHierarchy);

      if (companyWithHierarchy) {
        setCurrentHierarchyPath(companyWithHierarchy.hierarchyPath || [companyIdForAddress]);

        // Get display string
        const display = await hierarchyService.getHierarchyPathDisplay(
          companyWithHierarchy.hierarchyPath || [companyIdForAddress]
        );
        setCurrentHierarchyPathDisplay(display);
      }
    } catch (err) {
      console.error('Error loading current company path:', err);
    }
  }, [companyIdForAddress]);

  // Load data when user changes
  useEffect(() => {
    loadHierarchyData();
  }, [loadHierarchyData]);

  // Load current company path when company changes
  useEffect(() => {
    loadCurrentCompanyPath();
  }, [loadCurrentCompanyPath]);

  // ============================================
  // ACTIONS
  // ============================================

  /**
   * Refresh hierarchy data
   */
  const refreshHierarchy = useCallback(async () => {
    await loadHierarchyData();
    await loadCurrentCompanyPath();
  }, [loadHierarchyData, loadCurrentCompanyPath]);

  /**
   * Get company IDs for data queries based on current visibility settings
   * @param {string} companyId - Override company ID (optional)
   * @returns {Promise<Array|null>} Array of company IDs or null for no filter
   */
  const getQueryCompanyIds = useCallback(async (companyId = null) => {
    const targetCompanyId = companyId || companyIdForAddress;
    return hierarchyService.getQueryCompanyIds(currentUser, targetCompanyId);
  }, [currentUser, companyIdForAddress]);

  /**
   * Check if user can access a specific company
   * @param {string} companyId - Company ID to check
   * @returns {boolean}
   */
  const canAccessCompany = useCallback((companyId) => {
    if (userRole === 'superadmin') return true;
    return visibleCompanyIds.includes(companyId);
  }, [userRole, visibleCompanyIds]);

  /**
   * Check if user can create a division under a company
   * @param {string} parentCompanyId - Parent company ID
   * @returns {Promise<boolean>}
   */
  const canCreateUnder = useCallback(async (parentCompanyId) => {
    return hierarchyService.canUserCreateUnder(currentUser, parentCompanyId);
  }, [currentUser]);

  /**
   * Create a new division under a parent company
   * @param {Object} companyData - New company data
   * @param {string} parentCompanyId - Parent company ID
   * @returns {Promise<Object>} Created company
   */
  const createDivision = useCallback(async (companyData, parentCompanyId) => {
    const result = await hierarchyService.createDivision(companyData, parentCompanyId);
    await refreshHierarchy();
    return result;
  }, [refreshHierarchy]);

  /**
   * Move a company to a new parent
   * @param {string} companyId - Company to move
   * @param {string} newParentId - New parent company ID
   * @returns {Promise<void>}
   */
  const moveCompany = useCallback(async (companyId, newParentId) => {
    await hierarchyService.moveCompany(companyId, newParentId);
    await refreshHierarchy();
  }, [refreshHierarchy]);

  /**
   * Get ancestors of a company (for breadcrumb)
   * @param {string} companyId - Company ID
   * @returns {Promise<Array>}
   */
  const getAncestors = useCallback(async (companyId) => {
    return hierarchyService.getCompanyAncestors(companyId);
  }, []);

  /**
   * Get descendants of a company
   * @param {string} companyId - Company ID
   * @param {number} maxDepth - Maximum depth (optional)
   * @returns {Promise<Array>}
   */
  const getDescendants = useCallback(async (companyId, maxDepth = null) => {
    return hierarchyService.getCompanyDescendants(companyId, maxDepth);
  }, []);

  /**
   * Find a company in the flattened tree by ID
   * @param {string} companyId - Company ID to find
   * @returns {Object|null}
   */
  const findCompanyById = useCallback((companyId) => {
    return flattenedTree.find(c => c.companyID === companyId) || null;
  }, [flattenedTree]);

  /**
   * Get tree starting from a specific company
   * @param {string} companyId - Root company ID
   * @returns {Promise<Array>}
   */
  const getSubTree = useCallback(async (companyId) => {
    return hierarchyService.getHierarchyTree(companyId);
  }, []);

  // ============================================
  // CONTEXT VALUE
  // ============================================

  const value = useMemo(() => ({
    // State
    hierarchyTree,
    flattenedTree,
    visibleCompanyIds,
    visibleCompanies,
    currentHierarchyPath,
    currentHierarchyPathDisplay,
    currentCompanyWithHierarchy,
    loading,
    error,

    // Computed values
    hasHierarchyAccess,
    hierarchyEnabled,
    accessMode,
    dataVisibility,
    canCreateDivisions,

    // Helper functions
    getCompanyChildren,
    hasChildren,
    findCompanyById,
    canAccessCompany,

    // Async actions
    refreshHierarchy,
    getQueryCompanyIds,
    canCreateUnder,
    createDivision,
    moveCompany,
    getAncestors,
    getDescendants,
    getSubTree,

    // Constants (re-exported for convenience)
    ACCESS_MODES: hierarchyService.ACCESS_MODES,
    DATA_VISIBILITY: hierarchyService.DATA_VISIBILITY,
    BILLING_MODES: hierarchyService.BILLING_MODES
  }), [
    hierarchyTree,
    flattenedTree,
    visibleCompanyIds,
    visibleCompanies,
    currentHierarchyPath,
    currentHierarchyPathDisplay,
    currentCompanyWithHierarchy,
    loading,
    error,
    hasHierarchyAccess,
    hierarchyEnabled,
    accessMode,
    dataVisibility,
    canCreateDivisions,
    getCompanyChildren,
    hasChildren,
    findCompanyById,
    canAccessCompany,
    refreshHierarchy,
    getQueryCompanyIds,
    canCreateUnder,
    createDivision,
    moveCompany,
    getAncestors,
    getDescendants,
    getSubTree
  ]);

  return (
    <HierarchyContext.Provider value={value}>
      {children}
    </HierarchyContext.Provider>
  );
};

export default HierarchyContext;
